import { useCallback, useEffect, useRef, useState } from "react";
import config from "@/config";
import type {
  IntakeCase,
  IntakeDay,
  IntakeDayResponse,
  IntakeDaysResponse,
} from "@/types/intake";

const DEFAULT_TZ = "America/New_York";

function todayLocal(tz: string = DEFAULT_TZ): string {
  try {
    const fmt = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    return fmt.format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

export { DEFAULT_TZ as INTAKE_DEFAULT_TZ, todayLocal as todayLocalDate };

/**
 * ``lookback`` for ``/api/dashboard/intake/days``: inclusive calendar days from
 * Monday through ``ymd`` in ``tz`` (ISO week: Monday is first day), matching
 * how the backend counts ``lookback`` ending on that end date.
 */
export function intakeWeekToDateLookbackDays(
  ymd: string,
  tz: string = DEFAULT_TZ,
): number {
  const re = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (!re) return 1;
  const y = Number(re[1]);
  const mo = Number(re[2]);
  const d = Number(re[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) {
    return 1;
  }
  const pad = (n: number, w: number) => String(n).padStart(w, "0");
  const target = `${pad(y, 4)}-${pad(mo, 2)}-${pad(d, 2)}`;

  const dateFmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const ymdAtUtcMs = (utcMs: number): string => {
    let yy = "";
    let mm = "";
    let dd = "";
    for (const p of dateFmt.formatToParts(new Date(utcMs))) {
      if (p.type === "year") yy = p.value;
      else if (p.type === "month") mm = p.value;
      else if (p.type === "day") dd = p.value;
    }
    return `${yy}-${mm}-${dd}`;
  };

  let t = Date.UTC(y, mo - 1, d, 12, 0, 0);
  for (let i = 0; i < 48; i++) {
    if (ymdAtUtcMs(t) === target) break;
    if (ymdAtUtcMs(t) < target) t += 60 * 60 * 1000;
    else t -= 60 * 60 * 1000;
  }

  const weekday = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    weekday: "long",
  }).format(new Date(t));

  const monday0 = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ].indexOf(weekday);

  return Math.min(60, Math.max(1, monday0 + 1));
}

export interface UseIntakeDaysOptions {
  tz?: string;
  /** When true, only dashboard-excluded payers (e.g. Medicare) are included. */
  showHidden?: boolean;
}

/** GET /api/dashboard/intake/days */
export function useIntakeDays(
  lookback: number = 14,
  tzOrOptions: string | UseIntakeDaysOptions = DEFAULT_TZ,
  options?: UseIntakeDaysOptions,
) {
  const resolved =
    typeof tzOrOptions === "string"
      ? { tz: tzOrOptions, showHidden: options?.showHidden ?? false }
      : {
          tz: tzOrOptions.tz ?? DEFAULT_TZ,
          showHidden: tzOrOptions.showHidden ?? false,
        };
  const { tz, showHidden } = resolved;

  const [days, setDays] = useState<IntakeDay[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const seqRef = useRef(0);

  const fetchDays = useCallback(async () => {
    const seq = ++seqRef.current;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        lookback: String(lookback),
        tz,
      });
      if (showHidden) params.set("show_hidden", "true");
      const res = await fetch(
        `${config.API_BASE_URL}/api/dashboard/intake/days?${params.toString()}`,
      );
      if (!res.ok) throw new Error(`intake/days failed: ${res.status}`);
      const data = (await res.json()) as IntakeDaysResponse;
      if (seq !== seqRef.current) return;
      setDays(data.days);
    } catch (err) {
      if (seq !== seqRef.current) return;
      setError(err instanceof Error ? err.message : "Failed to load intake days");
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [lookback, tz, showHidden]);

  useEffect(() => {
    void fetchDays();
  }, [fetchDays]);

  return { days, loading, error, refresh: fetchDays };
}

export interface UseIntakeDayOptions {
  pageSize?: number;
  /** Days back to scan for cross-day duplicate detection (0 = skip). */
  duplicateLookback?: number;
  tz?: string;
  /** When true, only dashboard-excluded payers (e.g. Medicare) are included. */
  showHidden?: boolean;
}

/**
 * Paginated GET /api/dashboard/intake/day.
 *
 * Returns the case page-stream with a `loadMore` callback that fetches the
 * next page using the backend's `next_cursor` (created_at). Refetches from
 * scratch when the date changes.
 */
export function useIntakeDay(
  date: string | null,
  options: UseIntakeDayOptions = {},
) {
  const {
    pageSize = 50,
    duplicateLookback = 14,
    tz = DEFAULT_TZ,
    showHidden = false,
  } = options;

  const [cases, setCases] = useState<IntakeCase[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const cursorRef = useRef<string | null>(null);
  const seqRef = useRef(0);
  const dupAppliedRef = useRef(false);

  const fetchPage = useCallback(
    async (append: boolean) => {
      if (!date) return;
      const seq = ++seqRef.current;
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setCases([]);
        cursorRef.current = null;
        dupAppliedRef.current = false;
      }
      setError(null);
      try {
        const params = new URLSearchParams({
          date,
          tz,
          limit: String(pageSize),
          // Only run the (more expensive) duplicate-detection scan on the
          // first page so subsequent "Load more" calls stay fast.
          duplicate_lookback: String(append ? 0 : duplicateLookback),
        });
        if (showHidden) params.set("show_hidden", "true");
        if (append && cursorRef.current) {
          params.set("starting_after", cursorRef.current);
        }
        const res = await fetch(
          `${config.API_BASE_URL}/api/dashboard/intake/day?${params.toString()}`,
        );
        if (!res.ok) throw new Error(`intake/day failed: ${res.status}`);
        const next = (await res.json()) as IntakeDayResponse;
        if (seq !== seqRef.current) return;
        if (next.duplicate_detection_applied) dupAppliedRef.current = true;
        setCases((prev) => (append ? [...prev, ...next.cases] : next.cases));
        setHasMore(next.has_more);
        cursorRef.current = next.next_cursor ?? null;
      } catch (err) {
        if (seq !== seqRef.current) return;
        setError(
          err instanceof Error ? err.message : "Failed to load intake for date",
        );
      } finally {
        if (seq === seqRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [date, tz, pageSize, duplicateLookback, showHidden],
  );

  useEffect(() => {
    void fetchPage(false);
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) void fetchPage(true);
  }, [fetchPage, loadingMore, hasMore]);

  const refresh = useCallback(() => {
    void fetchPage(false);
  }, [fetchPage]);

  return {
    cases,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    refresh,
    duplicateDetectionApplied: dupAppliedRef.current,
  };
}
